import React, { useState, useRef } from 'react';
import { Card, Form, Input, Button, Radio, Select, Checkbox, Steps, Typography, message, Divider, Space, Spin } from 'antd';
import type ReCAPTCHA from 'react-google-recaptcha';
import { CreditCardOutlined, UserOutlined, HomeOutlined, CheckCircleOutlined, BankOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import { RecaptchaField } from '../../components/common/RecaptchaField';
import { sanitizeHtml } from '../../utils/sanitize';

import { PageHero } from '../../components/common/PageHero';

const { Title, Paragraph, Text } = Typography;

const Donate: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    // Fetch page metadata and update title
    usePageMetadata('donate');

    const { data: donateInfo, isLoading } = useQuery({
        queryKey: ['publicDonate', i18n.language],
        queryFn: async () => {
            const response = await apiClient.get(`/public/donate?lang=${i18n.language}`);
            return response.data.data;
        }
    });

    const next = async () => {
        try {
            // Validate only fields in the current step
            const fieldGroups = [
                ['amount', 'customAmount', 'frequency'],
                ['firstName', 'lastName', 'email', 'phone'],
                ['address', 'zip', 'city', 'country'],
                ['paymentMethod', 'sepa_accepted', 'sepa_accountHolder', 'sepa_iban', 'sepa_bic', 'sepa_bankCode', 'sepa_mandate', 'terms']
            ];
            
            await form.validateFields(fieldGroups[currentStep]);
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
    };

    const onFinish = async (values: any) => {
        try {
            // Manual reCAPTCHA execution for invisible mode
            const token = await recaptchaRef.current?.executeAsync();
            if (!token) {
                messageApi.error(t('verify_recaptcha', 'Bitte bestätigen Sie, dass Sie kein Roboter sind'));
                return;
            }

            await apiClient.post('/public/donate/submit', { ...values, recaptchaToken: token });
            messageApi.success(t('donation_success_msg', 'Vielen Dank für Ihre Unterstützung! Wir werden uns in Kürze bei Ihnen melden.'));
            setCurrentStep(4); // Success step
        } catch (error) {
            console.error('Error submitting donation:', error);
            messageApi.error(t('donation_error_msg', 'Es gab ein Problem bei der Verarbeitung Ihrer Spende. Bitte versuchen Sie es später erneut.'));
        }
    };

    if (isLoading || !donateInfo) return <div className="p-20 text-center"><Spin size="large" /></div>;

    const steps = [
        { title: t('donation_amount', 'Betrag'), icon: <CreditCardOutlined /> },
        { title: t('personal_info', 'Persönlich'), icon: <UserOutlined /> },
        { title: t('address', 'Adresse'), icon: <HomeOutlined /> },
        { title: t('payment', 'Zahlung'), icon: <BankOutlined /> },
    ];

    return (
        <div className="flex flex-col bg-background min-h-screen">
            <PageHero
                title={donateInfo?.title || t('donate_title', 'Spenden')}
                subtitle={t('donate_subtitle', 'Unterstützen Sie unsere Bildungs- und Kulturprojekte mit Ihrer Spende')}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: t('donate_breadcrumb', 'Spenden') }
                ]}
            />

            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 w-full">
                {contextHolder}
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
                    
                    {/* Information Column */}
                    <div>
                        <Paragraph className="text-lg text-secondary-foreground/80 leading-relaxed mb-8">
                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(donateInfo?.description || '') }} />
                        </Paragraph>

                    <Card className="bg-secondary/50 border-primary/20 shadow-sm">
                        <Title level={4}><BankOutlined className="text-primary mr-2" /> {t('bank_transfer_info', 'Bankverbindung')}</Title>
                        <Divider className="my-3" />
                        <Space orientation="vertical" className="w-full">
                            <div><Text type="secondary">{t('account_holder', 'Kontoinhaber')}:</Text> <br/><Text strong>{donateInfo.accountHolder}</Text></div>
                            <div><Text type="secondary">IBAN:</Text> <br/><Text strong className="text-lg">{donateInfo.iban}</Text></div>
                            <div><Text type="secondary">{t('bank', 'Bank')}:</Text> <br/><Text strong>{donateInfo.bankName}</Text></div>
                            <Divider className="my-2" />
                            <div className="grid grid-cols-2 gap-4">
                                <div><Text type="secondary">{t('register_no', 'Register-Nr')}:</Text> <br/><Text>{donateInfo.registerNumber}</Text></div>
                                <div><Text type="secondary">{t('local_court', 'Amtsgericht')}:</Text> <br/><Text>{donateInfo.localCourt}</Text></div>
                            </div>
                            <div><Text type="secondary">{t('chairman', 'Vorsitzender')}:</Text> <br/><Text>{donateInfo.chairmanName}</Text></div>
                        </Space>
                    </Card>

                    <div className="mt-8 p-4 bg-primary/5 border-l-4 border-primary rounded-r">
                        <Text italic className="text-sm opacity-80">
                            <InfoCircleOutlined className="mr-2" />
                            {t('donation_tax_info', 'Spenden an Mosaik e.V. sind steuerlich absetzbar. Auf Wunsch stellen wir Ihnen gerne eine Spendenbescheinigung aus.')}
                        </Text>
                    </div>
                </div>

                {/* Form Column */}
                <div>
                    <Card className="shadow-lg border-none overflow-hidden p-0">
                        <div className="bg-primary p-4 sm:p-6 text-white mb-8 overflow-x-auto">
                            <Steps current={currentStep} items={steps} className="donate-steps min-w-[320px]" responsive={false} labelPlacement="vertical" />
                        </div>

                        <Form 
                            form={form} 
                            layout="vertical" 
                            onFinish={onFinish} 
                            requiredMark="optional"
                            initialValues={{ amount: '20', frequency: 'once', country: 'Deutschland', wantsReceipt: true }}
                            className="px-6 pb-6"
                        >
                            <div className={currentStep === 0 ? "fade-in block" : "hidden"}>
                                <Title level={4} className="mb-4">{t('choose_amount', 'Wählen Sie Ihren Spendenbetrag')}</Title>
                                    <Form.Item name="amount" rules={[{ required: true }]}>
                                        <Radio.Group buttonStyle="solid" className="w-full grid grid-cols-3 gap-3">
                                            <Radio.Button value="10" className="text-center h-12 flex items-center justify-center">10 €</Radio.Button>
                                            <Radio.Button value="20" className="text-center h-12 flex items-center justify-center">20 €</Radio.Button>
                                            <Radio.Button value="50" className="text-center h-12 flex items-center justify-center">50 €</Radio.Button>
                                            <Radio.Button value="100" className="text-center h-12 flex items-center justify-center">100 €</Radio.Button>
                                            <Radio.Button value="200" className="text-center h-12 flex items-center justify-center">200 €</Radio.Button>
                                            <Radio.Button value="custom" className="text-center h-12 flex items-center justify-center">{t('custom', 'Anderer')}</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                    
                                    <Form.Item noStyle shouldUpdate={(prevVal, currVal) => prevVal.amount !== currVal.amount}>
                                        {({ getFieldValue }) => getFieldValue('amount') === 'custom' ? (
                                            <Form.Item name="customAmount" rules={[{ required: true, message: t('enter_amount', 'Bitte Betrag eingeben') }]}>
                                                <Input prefix="€" size="large" placeholder="0,00" />
                                            </Form.Item>
                                        ) : null}
                                    </Form.Item>

                                    <Form.Item label={t('frequency', 'Zahlungsweise')} name="frequency">
                                        <Select size="large">
                                            <Select.Option value="once">{t('once', 'Einmalig')}</Select.Option>
                                            <Select.Option value="monthly">{t('monthly', 'Monatlich')}</Select.Option>
                                            <Select.Option value="quarterly">{t('quarterly', 'Vierteljährlich')}</Select.Option>
                                            <Select.Option value="yearly">{t('yearly', 'Jährlich')}</Select.Option>
                                        </Select>
                                    </Form.Item>

                                    <Button type="primary" size="large" block onClick={next} className="h-12 mt-4">
                                        {t('next', 'Weiter')}
                                    </Button>
                            </div>

                            <div className={currentStep === 1 ? "fade-in block" : "hidden"}>
                                <Title level={4} className="mb-4">{t('personal_details', 'Ihre Daten')}</Title>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Form.Item label={t('firstname', 'Vorname')} name="firstName" rules={[{ required: true }]}>
                                            <Input size="large" />
                                        </Form.Item>
                                        <Form.Item label={t('lastname', 'Nachname')} name="lastName" rules={[{ required: true }]}>
                                            <Input size="large" />
                                        </Form.Item>
                                    </div>
                                    <Form.Item label={t('email', 'E-Mail-Adresse')} name="email" rules={[{ required: true, type: 'email' }]}>
                                        <Input size="large" />
                                    </Form.Item>
                                    <Form.Item label={t('phone', 'Telefon (optional)')} name="phone">
                                        <Input size="large" />
                                    </Form.Item>
                                    <Form.Item name="wantsReceipt" valuePropName="checked" className="mt-4">
                                        <Checkbox className="text-base font-medium">{t('wants_receipt', 'Ich benötige eine Spendenbescheinigung')}</Checkbox>
                                    </Form.Item>
                                    
                                <Space className="w-full justify-between mt-4">
                                    <Button size="large" onClick={prev}>{t('back', 'Zurück')}</Button>
                                    <Button type="primary" size="large" onClick={next}>{t('next', 'Weiter')}</Button>
                                </Space>
                            </div>

                            <div className={currentStep === 2 ? "fade-in block" : "hidden"}>
                                <Title level={4} className="mb-4">{t('billing_address', 'Anschrift für Spendenquittung')}</Title>
                                    <Form.Item label={t('street', 'Straße & Hausnummer')} name="address" rules={[{ required: true }]}>
                                        <Input size="large" />
                                    </Form.Item>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Form.Item label={t('zip', 'PLZ')} name="zip" rules={[{ required: true }]}>
                                            <Input size="large" />
                                        </Form.Item>
                                        <Form.Item label={t('city', 'Ort')} name="city" rules={[{ required: true }]}>
                                            <Input size="large" />
                                        </Form.Item>
                                    </div>
                                    <Form.Item label={t('country', 'Land')} name="country">
                                        <Select size="large">
                                            <Select.Option value="Deutschland">Deutschland</Select.Option>
                                            <Select.Option value="Türkei">Türkei</Select.Option>
                                            <Select.Option value="Österreich">Österreich</Select.Option>
                                            <Select.Option value="Schweiz">Schweiz</Select.Option>
                                        </Select>
                                    </Form.Item>
                                    
                                <Space className="w-full justify-between mt-6">
                                    <Button size="large" onClick={prev}>{t('back', 'Zurück')}</Button>
                                    <Button type="primary" size="large" onClick={next}>{t('next', 'Weiter')}</Button>
                                </Space>
                            </div>

                            <div className={currentStep === 3 ? "fade-in block" : "hidden"}>
                                <Title level={4} className="mb-4">{t('payment_method', 'Zahlungsweise')}</Title>
                                    
                                    <Form.Item name="paymentMethod" rules={[{ required: true }]} initialValue="transfer">
                                        <Radio.Group className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <Radio.Button value="transfer" className="h-14 flex items-center justify-center text-base">{t('bank_transfer', 'Überweisung')}</Radio.Button>
                                            <Radio.Button value="sepa" className="h-14 flex items-center justify-center text-base">{t('sepa_direct_debit', 'SEPA Lastschrift')}</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item noStyle shouldUpdate={(prevVal, currVal) => prevVal.paymentMethod !== currVal.paymentMethod}>
                                        {({ getFieldValue }) => getFieldValue('paymentMethod') === 'sepa' ? (
                                            <div className="mt-6 p-6 bg-secondary/30 rounded-lg border border-primary/20">
                                                <Title level={5} className="mb-4">{t('sepa_direct_debit', 'SEPA Lastschrift')}</Title>
                                                
                                                <Form.Item name="sepa_accepted" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(t('accept_sepa', 'Bitte akzeptieren Sie die SEPA-Zahlung')) }]}>
                                                    <Checkbox className="text-base font-medium">{t('sepa_accept_text', 'Ich akzeptiere die Zahlung per SEPA-Lastschrift.')}</Checkbox>
                                                </Form.Item>
                                                
                                                <div className="mt-4 mb-2"><Text strong>{t('bank_details', 'Bankverbindung')}:</Text></div>
                                                
                                                <Form.Item label={`${t('account_holder', 'Kontoinhaber')} *`} name="sepa_accountHolder" rules={[{ required: true }]}>
                                                    <Input size="large" placeholder={`${t('account_holder', 'Kontoinhaber')} *`} />
                                                </Form.Item>
                                                
                                                <Form.Item label="IBAN *" name="sepa_iban" rules={[{ required: true }]}>
                                                    <Input size="large" placeholder="IBAN" />
                                                </Form.Item>
                                                
                                                <div className="grid grid-cols-2 gap-4">
                                                    <Form.Item label="BIC/Swift" name="sepa_bic">
                                                        <Input size="large" placeholder="BIC/Swift" />
                                                    </Form.Item>
                                                    <Form.Item label={`${t('bank_code', 'Bankleitzahl')}`} name="sepa_bankCode">
                                                        <Input size="large" placeholder={t('bank_code', 'Bankleitzahl')} />
                                                    </Form.Item>
                                                </div>
                                                
                                                <Form.Item name="sepa_mandate" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(t('accept_mandate', 'Bitte bestätigen Sie das Mandat')) }]} className="mt-6 mb-0">
                                                    <Checkbox>{t('sepa_mandate_text', 'Hiermit stimme ich der Abbuchung zu und bestätige, dass ich Kontoinhaber/-in bin.')}</Checkbox>
                                                </Form.Item>
                                            </div>
                                        ) : (
                                            <div className="mt-6 p-4 bg-primary/5 rounded border-l-4 border-primary">
                                                <Text>{t('transfer_instructions', 'Bitte überweisen Sie den Betrag nach Abschluss des Formulars auf unser im Informationsblock angegebenes Spendenkonto.')}</Text>
                                            </div>
                                        )}
                                    </Form.Item>

                                    <Form.Item name="message" label={t('message_optional', 'Nachricht an uns (optional)')} className="mt-6">
                                        <Input.TextArea rows={3} />
                                    </Form.Item>

                                    <Form.Item name="recaptchaToken">
                                        <RecaptchaField ref={recaptchaRef} />
                                    </Form.Item>

                                    <Form.Item name="terms" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(t('accept_terms', 'Bitte akzeptieren Sie die Bedingungen')) }]}>
                                        <Checkbox className="text-xs">
                                            {t('privacy_consent', 'Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung meiner Daten zu.')}
                                        </Checkbox>
                                    </Form.Item>

                                <Space className="w-full justify-between">
                                    <Button size="large" onClick={prev}>{t('back', 'Zurück')}</Button>
                                    <Button type="primary" size="large" htmlType="submit">
                                        {t('submit_donation', 'Jetzt Spenden')}
                                    </Button>
                                </Space>
                            </div>

                            <div className={currentStep === 4 ? "text-center py-12 fade-in block" : "hidden"}>
                                <CheckCircleOutlined className="text-6xl text-success mb-6" />
                                    <Title level={3}>{t('thank_you', 'Vielen Dank!')}</Title>
                                    <Paragraph className="text-lg opacity-80 mb-8">
                                        {t('donation_received_msg', 'Ihre Spende hilft uns, Mosaiksteine zu einem harmonischen Bild zusammenzufügen.')}
                                    </Paragraph>
                                <Button type="primary" size="large" href="/">
                                    {t('back_to_home', 'Zurück zur Startseite')}
                                </Button>
                            </div>
                        </Form>
                    </Card>
                </div>
            </div>
        </div>
    </div>
    );
};

export default Donate;
